/**
 * <slider-autoplay> — auto-advance for a horizontally-scrolling slider.
 *
 * Steps a scroll container one page (a viewport-width step) at a time and, once
 * the last page is reached, scrolls back to the first one and starts over.
 * Engine-agnostic — it only drives scrollLeft, so the DOM is untouched: no
 * cloned slides, and <slider-dots> stays in sync for free.
 *
 * Attributes:
 *   data-scroller — CSS selector for the scroll container. Resolved against the
 *                   previous element sibling first, then the closest section.
 *   data-speed    — milliseconds between steps. Defaults to 4000.
 *
 * No-op when the scroller can't be resolved, when it doesn't overflow (nothing
 * to advance), or when the visitor asked for reduced motion. Also idles while
 * the slider is off-screen or the tab is hidden, and while the visitor is
 * interacting with it (pointer/touch/keyboard focus), resuming afterwards.
 */
class SliderAutoplay extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number} */
  #speed = 4000;

  /** @type {number | null} */
  #timer = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #visible = false;

  /** @type {boolean} */
  #interacting = false;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  connectedCallback() {
    const selector = this.dataset.scroller;
    if (!selector) return;

    const prev = this.previousElementSibling;
    let scroller = prev instanceof HTMLElement && prev.matches(selector) ? prev : null;
    if (!scroller) {
      const scope = this.closest('.section') || this.parentElement || document;
      const found = scope.querySelector(selector);
      if (found instanceof HTMLElement) scroller = found;
    }
    if (!scroller) return;

    this.#scroller = scroller;

    const speed = parseFloat(this.dataset.speed ?? '');
    if (Number.isFinite(speed) && speed > 0) this.#speed = speed;

    // Auto-motion the visitor did not ask for is exactly what this query is
    // about, so honouring it means not starting at all.
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#sync);

    // Advancing a slider nobody is looking at burns main thread and, worse,
    // moves the slide out from under a visitor who scrolls back to it.
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, { threshold: 0.2 });
    this.#intersectionObserver.observe(scroller);

    document.addEventListener('visibilitychange', this.#sync);

    for (const event of ['pointerdown', 'touchstart', 'focusin', 'mouseenter']) {
      scroller.addEventListener(event, this.#pause, { passive: true });
    }
    for (const event of ['pointerup', 'touchend', 'focusout', 'mouseleave']) {
      scroller.addEventListener(event, this.#resume, { passive: true });
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#intersectionObserver?.disconnect();
    this.#reducedMotion?.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#sync);

    const scroller = this.#scroller;
    if (!scroller) return;
    for (const event of ['pointerdown', 'touchstart', 'focusin', 'mouseenter']) {
      scroller.removeEventListener(event, this.#pause);
    }
    for (const event of ['pointerup', 'touchend', 'focusout', 'mouseleave']) {
      scroller.removeEventListener(event, this.#resume);
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const entry = entries[0];
    if (!entry) return;
    this.#visible = entry.isIntersecting;
    this.#sync();
  };

  #pause = () => {
    this.#interacting = true;
    this.#stop();
  };

  #resume = () => {
    this.#interacting = false;
    this.#sync();
  };

  #sync = () => {
    const shouldRun =
      this.#visible &&
      !this.#interacting &&
      !document.hidden &&
      this.#reducedMotion?.matches !== true;

    if (shouldRun) this.#start();
    else this.#stop();
  };

  #start() {
    if (this.#timer !== null) return;
    this.#timer = window.setInterval(this.#step, this.#speed);
  }

  #stop() {
    if (this.#timer === null) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #step = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const page = scroller.clientWidth;
    // Not a scroller at this viewport (e.g. the row is only a slider on mobile),
    // so there is no page to advance to.
    if (page === 0 || scroller.scrollWidth - page <= 1) return;

    // 1px of slack absorbs the sub-pixel rounding of scroll-snap: without it the
    // last page can report a scrollLeft a fraction short of the maximum and the
    // rewind never fires.
    const atEnd = scroller.scrollLeft + page >= scroller.scrollWidth - 1;
    scroller.scrollTo({ left: atEnd ? 0 : scroller.scrollLeft + page, behavior: 'smooth' });
  };
}

if (!customElements.get('slider-autoplay')) {
  customElements.define('slider-autoplay', SliderAutoplay);
}
